import { Outlet } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { SIDEBAR_COOKIE_NAME, SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from './app-sidebar';
import { RequireAuth } from './require-auth';
import { SiteHeader } from './site-header';

/**
 * The remembered sidebar state. `SidebarProvider` WRITES `sidebar_state=true|false` on every toggle
 * (ui/sidebar.tsx) but — being the Vite port of a Next.js component — never READS it back; the Next
 * version reads the cookie server-side into `defaultOpen`. Until this helper existed the collapsed
 * rail was forgotten on every reload (and, while each route mounted its own shell, on every click).
 * Absent, malformed or unreadable → `undefined`, and the provider falls back to its default (open).
 */
export function readSidebarCookie(cookie?: string): boolean | undefined {
  try {
    const source = cookie ?? document.cookie;
    for (const part of source.split(';')) {
      const [name, value] = part.trim().split('=');
      if (name !== SIDEBAR_COOKIE_NAME) continue;
      if (value === 'true') return true;
      if (value === 'false') return false;
      return undefined;
    }
  } catch {
    // A sandboxed or cookie-blocked context throws on access; the default is the right answer there.
  }
  return undefined;
}

export function AppShell({ children }: { children: ReactNode }) {
  return (
    <RequireAuth>
      <SidebarProvider defaultOpen={readSidebarCookie() ?? true}>
        <AppSidebar />
        {/* ⚠️ `SidebarInset` IS a <main> (ui/sidebar.tsx:294), so the inner wrapper must not be one too
            — two `main` landmarks is ambiguous for a screen reader's landmark navigation, and there is
            no second "main content" here to justify it. Nothing in the app queries `role="main"`
            (grep-verified), so this is a pure semantics fix with no behavioural surface. */}
        <SidebarInset>
          <SiteHeader />
          <div className="flex-1 px-4 py-6 sm:px-6 lg:px-8">{children}</div>
        </SidebarInset>
      </SidebarProvider>
    </RequireAuth>
  );
}

/**
 * The component of the pathless `_shell` layout route (router.tsx): the shell, mounted once, around
 * whichever child route matched. Routes nested under it render into the Outlet; the shell — and the
 * sidebar state inside it — persists while the operator moves between them.
 */
export function ShellLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
